import { Grid, TextField, FormControl, Button, Box, Select, MenuItem, InputLabel } from '@mui/material';
import { DoNotCountItemsTable } from './DoNotCountItemsTable';
import { useContext, useEffect, useState } from 'react';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { UserContext } from '../../../../components/shared/useUser';
import { DeleteStockCheckDoNotCountItemRequest, Item, PrimaryCategory } from '../../../../app/services/api/generated';
import LoadingWheel from '../../../../components/ui/LoadingWheel';
import DoNotCountItemsAddItemsModal from './modals/DoNotCountItemsAddItemsModal';
import { ConfirmationDialog } from '../../../../components/shared/dialogs/ConfirmationDialog';
import { Trash01 } from '../../../../assets';
import { theme } from '../../../../theme';
import DoNotCountItemsBulkAddItemsModal from './modals/DoNotCountItemsBulkAddItemsModal';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../components/ui/Section';

const NO_CATEGORY_VALUE = '';

export const DoNotCountItems = () => {
  const { t } = useTranslation('doNotCountItems');
  const { selectedStore } = useContext(UserContext);
  const { getDoNotCountItems, getCategories, deleteDoNotCountItems } = useNswagClient();
  const [items, setItems] = useState<Item[]>([]);
  const [page, setPage] = useState<number>(0);
  const [count, setCount] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [categories, setCategories] = useState<PrimaryCategory[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('-1');
  const [deleteItemNumber, setDeleteItemNumber] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAddItemsModalOpen, setIsAddItemsModalOpen] = useState<boolean>(false);
  const [isBulkAddItemsModalOpen, setIsBulkAddItemsModalOpen] = useState<boolean>(false);
  const [isDeleteItemsModalOpen, setIsDeleteItemsModalOpen] = useState<boolean>(false);
  const [fieldToSort, setFieldToSort] = useState<keyof Item | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const rowsPerPage = 10;

  useEffect(() => {
    fetchData();
    fetchTotalCount();
    getCategoriesForStore();
  }, []);

  useEffect(() => {
    fetchData();
  }, [page]);

  const searchTermChange = (value: string) => {
    setSearchTerm(value);
  };

  const searchClicked = () => {
    setPage(0);
    fetchData();
  };

  const fetchTotalCount = () => {
    getDoNotCountItems(selectedStore?.storeNumber, '', '-1', undefined, 'asc', 0, 1)
      .then((result) => {
        setTotalCount(result?.data?.itemCount ?? 0);
      })
      .catch((e) => console.error(e));
  };

  const getCategoriesForStore = () => {
    getCategories(selectedStore?.storeNumber ?? '-1')
      .catch((e) => console.error(e))
      .then((result) => {
        const categoriesFromApi = result?.data?.filter(item => item.stockItemCount ?? 0 > 0);
        setCategories(categoriesFromApi ?? []);
      });
  };

  const fetchData = (passedPage?: number, passedDirection?: 'asc' | 'desc', passedFeildToSort?: keyof Item, passedSearch?: string, passedCategory?: string) => {
    const pageToUse = passedPage ?? page;
    const directionToUse = passedDirection ?? sortDirection;
    const feildToSortToUse = passedFeildToSort ?? fieldToSort;
    const categoryValue = passedCategory ?? selectedCategory;
    const searchToUse = passedSearch ?? searchTerm;

    setIsLoading(true);
    getDoNotCountItems(selectedStore?.storeNumber, searchToUse, categoryValue, feildToSortToUse, directionToUse, pageToUse * rowsPerPage, rowsPerPage)
      .catch((e) => console.error(e))
      .then((result) => {
        setItems(result?.data?.items ?? []);
        setCount(result?.data?.itemCount ?? 0);
      })
      .finally(() => setIsLoading(false));
  };

  const DeleteIconClicked = (itemNumber: string) => {
    setDeleteItemNumber(itemNumber);
    setIsDeleteItemsModalOpen(true);
  };

  const handleOnDelete = () => {
    setIsDeleteItemsModalOpen(false);
    setIsLoading(true);
    const body: DeleteStockCheckDoNotCountItemRequest = { franchiseId: selectedStore?.franchiseId, itemNumber: deleteItemNumber };
    deleteDoNotCountItems(body)
      .catch((e) => console.error(e))
      .finally(() => {
        setPage(0);
        fetchData(0);
        fetchTotalCount();
      });
  };

  const handelSort = (property: keyof Item) => {
    const isDifferentFieldToSort = property != fieldToSort;

    let newDirection = sortDirection;
    if (isDifferentFieldToSort) {
      newDirection = 'asc';
      setSortDirection('asc');
      setFieldToSort(property);
    }
    else if (sortDirection === 'asc') {
      newDirection = 'desc';
      setSortDirection(newDirection);
    }
    else {
      setFieldToSort(undefined);
    }

    fetchData(page, newDirection, property);
  };

  const handleClearButtonClick: () => void = () => {
    searchTermChange('');
    setSelectedCategory('-1');
    setPage(0);
    fetchData(0, undefined, undefined, '', '-1');
  };

  const maxPages = () => count ? Math.ceil(count / rowsPerPage) : 0;

  return <Grid container
    spacing={4}
    sx={{
      pt: '8px',
    }} >
    <Grid container
      item
      sx={{ justifyContent: 'flex-end' }}
      xs={12}>
      <Button size='lg'
        onClick={() => setIsBulkAddItemsModalOpen(true)}>{t('bulkAddItem')}</Button>
      <Button size='lg'
        sx={{ ml: '4px' }}
        onClick={() => setIsAddItemsModalOpen(true)}>{t('addItem')}</Button>
    </Grid>
    <Grid item
      xs={12}>
      <Grid container
        spacing={4}>
        <Grid item
          sm={5}
          xs={12}>
          <FormControl fullWidth>
            <TextField label={t('search')}
              value={searchTerm}
              onChange={e => searchTermChange(e.target.value)} />
          </FormControl>
        </Grid>
        <Grid item
          sm={5}
          xs={12}>
          <FormControl fullWidth>
            <InputLabel shrink>{t('category')}</InputLabel>
            <Select value={selectedCategory}
              label={t('category')}
              displayEmpty
              onChange={(e) => (setSelectedCategory(e.target.value))}
              renderValue={(selected) => {
                if (selected === '-1') return t('all');
                if (selected === NO_CATEGORY_VALUE) return t('noCategory');
                return selected;
              }}>
              <MenuItem value="-1">
                <em>{t('all')}</em>
              </MenuItem>
              <MenuItem value={NO_CATEGORY_VALUE}>
                {t('noCategory')}
              </MenuItem>
              {categories.map(category => (<MenuItem key={category.primaryCategoryName}
                value={category.primaryCategoryName}>{category.primaryCategoryName}</MenuItem>))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item
          sm={2}
          xs={12}>
          <Button fullWidth
            size="lg"
            disabled={isLoading}
            onClick={searchClicked}>{t('search')}</Button>
        </Grid>
        <Grid item
          xs={12}>
          <Section>
            <DoNotCountItemsTable
              items={items}
              page={page}
              setPage={setPage}
              maxPages={maxPages()}
              handleOnDelete={DeleteIconClicked}
              handleSort={handelSort}
              sortDirection={sortDirection}
              fieldToSort={fieldToSort}
              isLoading={isLoading}
              searchTerm={searchTerm}
              handleClearButtonClick={handleClearButtonClick}
              handleConfirmModal={() => setIsAddItemsModalOpen(true)}
              totalCount={count}
            />
          </Section>
        </Grid>
      </Grid>

    </Grid>
    <DoNotCountItemsAddItemsModal isItemsModalOpen={isAddItemsModalOpen}
      handleClose={() => setIsAddItemsModalOpen(false)}
      reloadData={() => { fetchData(); fetchTotalCount(); }} />
    <DoNotCountItemsBulkAddItemsModal isItemsModalOpen={isBulkAddItemsModalOpen}
      handleClose={() => setIsBulkAddItemsModalOpen(false)}
      reloadData={() => { fetchData(); fetchTotalCount(); }}
      hasExistingItems={totalCount > 0} />
    <ConfirmationDialog icon={<Trash01 color={theme.palette.error[500]} />}
      title={t('deleteDoNotCountItem')}
      open={isDeleteItemsModalOpen}
      message={t('deleteDoNotCountItemMessage')}
      confirmButtonText={t('delete')}
      cancelButtonText={t('cancel')}
      onCancel={() => { setIsDeleteItemsModalOpen(false); }}
      onConfirm={handleOnDelete} />
  </Grid >;
};